import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import { queryKeys } from "../../api/queryKeys";
import {
  employeeListSchema,
  employmentDetailSchema,
  establishmentSchema,
  projectDetailSchema,
  projectListItemSchema,
  projectRequirementPolicyRemovalSchema,
  projectRequirementPolicySchema,
  teamDefinitionSchema,
} from "../../api/schemas";
import type {
  Employee,
  EmploymentDetail,
  Establishment,
  PolicyScopeKind,
  ProjectDetail,
  ProjectListItem,
  ProjectMember,
  ProjectRequirementMode,
  ProjectRequirementPolicy,
  TeamDefinition,
} from "../../api/schemas";
import type { LocalDate } from "../../domain/time";
import type { ProjectBody } from "./model/projectForms";

/** A responsibility as sent to `PUT /projects/{id}/responsibles`. */
export interface ResponsibleInput {
  readonly employeeId: string;
  readonly from: LocalDate;
  readonly to: LocalDate | null;
}

/** Body of `PUT /project-requirement-policies`. */
export interface PolicyInput {
  readonly scopeKind: PolicyScopeKind;
  readonly scopeId: string | null;
  readonly mode: ProjectRequirementMode;
  readonly allocationDeadlineWorkingDays: number;
  /** Whether project time also needs an activity (T-184); never blocking. */
  readonly requireActivity?: boolean;
}

/** A project change. */
export type ProjectChange =
  | { readonly kind: "save"; readonly id: string | null; readonly body: ProjectBody }
  | { readonly kind: "archive" | "reactivate"; readonly id: string }
  | { readonly kind: "members"; readonly id: string; readonly members: readonly ProjectMember[] }
  | {
      readonly kind: "responsibles";
      readonly id: string;
      readonly responsibles: readonly ResponsibleInput[];
    }
  | { readonly kind: "activities"; readonly id: string; readonly activityIds: readonly string[] };

/**
 * Loads the projects the current user manages (HR and managers all, project responsibles their
 * own), archived ones included, each with its participant counts (F-27).
 *
 * @param enabled - Whether to load them.
 * @returns The query result.
 */
export function useManagedProjects(enabled = true): UseQueryResult<ProjectListItem[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.projects,
    queryFn: ({ signal }) =>
      client.get("/api/v1/projects", projectListItemSchema.array(), {
        query: { includeArchived: "true" },
        signal,
      }),
    enabled,
  });

  return query;
}

/**
 * Loads a project with its members and responsibilities.
 *
 * @param projectId - The project.
 * @returns The query result.
 */
export function useProjectDetail(projectId: string): UseQueryResult<ProjectDetail> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.project(projectId),
    queryFn: ({ signal }) =>
      client.get(`/api/v1/projects/${encodeURIComponent(projectId)}`, projectDetailSchema, {
        signal,
      }),
  });

  return query;
}

/**
 * Loads the teams of the tenant to choose members from.
 *
 * @param enabled - Whether the person may read the teams.
 * @returns The query result.
 */
export function useTeamDefinitions(enabled: boolean): UseQueryResult<TeamDefinition[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.teamDefinitions,
    queryFn: ({ signal }) => client.get("/api/v1/teams", teamDefinitionSchema.array(), { signal }),
    enabled,
  });

  return query;
}

/**
 * Orders employees by display name, the order of every list to choose a person from (F-57).
 *
 * @param employees - The employees in the order of the API (personnel number).
 * @returns A sorted copy.
 */
export function employeesByName(employees: readonly Employee[]): Employee[] {
  const sorted = employees.toSorted((left, right) =>
    left.displayName.localeCompare(right.displayName),
  );

  return sorted;
}

/**
 * Loads the employees to choose members, responsibles and planners from (HR only), ordered by name.
 *
 * @param enabled - Whether the person may read the employees.
 * @returns The query result.
 */
export function useEmployeeChoices(enabled: boolean): UseQueryResult<Employee[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.employees,
    queryFn: ({ signal }) =>
      client.get("/api/v1/employees", employeeListSchema, { query: { take: 500 }, signal }),
    select: employeesByName,
    enabled,
    staleTime: 300_000,
  });

  return query;
}

/**
 * Loads the establishments to choose a policy scope from (HR only).
 *
 * @param enabled - Whether the person may read the establishments.
 * @returns The query result.
 */
export function useEstablishmentChoices(enabled: boolean): UseQueryResult<Establishment[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.establishments,
    queryFn: ({ signal }) =>
      client.get("/api/v1/establishments", establishmentSchema.array(), { signal }),
    enabled,
  });

  return query;
}

/**
 * Loads the employments of an employee to choose a policy scope from (HR only).
 *
 * @param employeeId - The employee, or `null` while none is chosen.
 * @returns The query result.
 */
export function useEmploymentChoices(
  employeeId: string | null,
): UseQueryResult<EmploymentDetail[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.employments(employeeId ?? ""),
    queryFn: ({ signal }) =>
      client.get(
        `/api/v1/employees/${encodeURIComponent(employeeId ?? "")}/employments`,
        employmentDetailSchema.array(),
        { signal },
      ),
    enabled: employeeId !== null,
  });

  return query;
}

/**
 * Loads the requirement policies the current user may see.
 *
 * @returns The query result.
 */
export function useRequirementPolicies(): UseQueryResult<ProjectRequirementPolicy[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.projectPolicies,
    queryFn: ({ signal }) =>
      client.get("/api/v1/project-requirement-policies", projectRequirementPolicySchema.array(), {
        signal,
      }),
  });

  return query;
}

/**
 * Creates the mutation for all project changes; it refreshes the project list, the changed
 * project and the selectable projects of the current user.
 *
 * @returns The mutation.
 */
export function useChangeProject(): UseMutationResult<ProjectDetail, Error, ProjectChange> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (change: ProjectChange) => {
      if (change.kind === "save") {
        const saved =
          change.id === null
            ? client.post("/api/v1/projects", change.body, projectDetailSchema)
            : client.put(
                `/api/v1/projects/${encodeURIComponent(change.id)}`,
                change.body,
                projectDetailSchema,
              );
        return saved;
      }

      const path = `/api/v1/projects/${encodeURIComponent(change.id)}`;

      if (change.kind === "members") {
        const members = client.put(`${path}/members`, change.members, projectDetailSchema);
        return members;
      }

      if (change.kind === "activities") {
        const activities = client.put(
          `${path}/activities`,
          change.activityIds,
          projectDetailSchema,
        );
        return activities;
      }

      if (change.kind === "responsibles") {
        const responsibles = client.put(
          `${path}/responsibles`,
          change.responsibles,
          projectDetailSchema,
        );
        return responsibles;
      }

      const lifecycle = client.post(`${path}:${change.kind}`, {}, projectDetailSchema);
      return lifecycle;
    },
    onSuccess: async (detail) => {
      queryClient.setQueryData(queryKeys.project(detail.project.id), detail);
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
      await queryClient.invalidateQueries({ queryKey: ["me", "projects"] });
    },
  });

  return mutation;
}

/**
 * Creates the mutation that sets a requirement policy.
 *
 * @returns The mutation.
 */
export function useSetPolicy(): UseMutationResult<ProjectRequirementPolicy, Error, PolicyInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: PolicyInput) =>
      client.put("/api/v1/project-requirement-policies", input, projectRequirementPolicySchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.projectPolicies }),
  });

  return mutation;
}

/**
 * Creates the mutation that removes a requirement policy.
 *
 * @returns The mutation.
 */
export function useRemovePolicy(): UseMutationResult<{ id: string }, Error, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (policyId: string) =>
      client.delete(
        `/api/v1/project-requirement-policies/${encodeURIComponent(policyId)}`,
        projectRequirementPolicyRemovalSchema,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.projectPolicies }),
  });

  return mutation;
}
